/**
 * CodeEditor — F12.B T12.B.3.
 * Prosta textarea z monospacją, numerami linii i wskazaniem języka.
 * Pełny syntax highlighting → deferred do F12.C.
 */
import { useCallback, useRef } from 'react';

interface CodeEditorProps {
  value: string;
  onChange: (code: string) => void;
  language?: string;
  placeholder?: string;
  rows?: number;
}

export function CodeEditor({
  value,
  onChange,
  language = 'javascript',
  placeholder = '',
  rows = 10,
}: CodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /** Tab key wstawia 2 spacje zamiast zmiany focusu. */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const newVal = value.substring(0, start) + '  ' + value.substring(end);
        onChange(newVal);
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + 2;
        });
      }
    },
    [value, onChange],
  );

  return (
    <div className="rounded-md border border-border-primary bg-bg-primary font-mono text-sm focus-within:border-accent-500 focus-within:ring-1 focus-within:ring-accent-500">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-primary px-3 py-1.5">
        <span className="text-xs font-medium text-text-muted">Kod</span>
        <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs text-text-muted">
          {language}
        </span>
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={rows}
        placeholder={placeholder}
        spellCheck={false}
        className="w-full resize-y bg-transparent px-3 py-2 text-text-primary placeholder:text-text-tertiary focus:outline-none"
      />
    </div>
  );
}
